import { Component } from 'react';
import classNames from 'classnames/bind';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import autoBind from 'react-autobind';
import moment from 'moment';
import differenceBy from 'lodash-es/differenceBy';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBookmark } from '@fortawesome/pro-light-svg-icons/faBookmark';
import { faLongArrowRight } from '@fortawesome/pro-light-svg-icons/faLongArrowRight';

import { isMobileOnly } from 'react-device-detect';
import * as storyActions from '../../actions/stories.actions';
import * as storyFiltersAction from '../../actions/storyFilters.action';
import * as recommendedStoriesAction from '../../actions/recommendedStories.actions';
import withComponentName from '../../decorators/withComponentName';

import { initialState, sentimentValuesByText } from '../../data/filters/storyFilters';
import { SILVER, GOLD } from '../../data/permissions';
import { CUSTOM_TIME_FILTER } from '../../data/features';
import { periodTimeValues } from '../../data/directory/locations/locationsSentiment';

import StorageSvc from '../../services/StorageSvc';
import LanguagesSvc from '../../services/dbServices/LanguagesSvc';

import StoryCard from '../TrendingStories/StoryCard';
import Preloader from '../Preloader';
import StoryCaughtUp from '../StoryCaughtUp';

import permissionsDecorator from '../../decorators/permissionsDecorator';

import Styles from './styles.module.scss';

const cx = classNames.bind(Styles);

export class RecommendedStories extends Component {
  constructor(props) {
    super(props);
    this.state = {
      resetTimeFilter: false,
      prevTimeFilter: '',
      nextTimeFilter: '',
      isSentimentReset: false,
      sentimentResetText: '',
    };
    this.mounted = true;
    autoBind(this);
  }

  componentDidMount() {
    this.fetchMoreStories();
    this.checkTimeFilter();
    const { isSentimentReset, sentimentResetText = '' } = this.setSentimentReset();
    this.setState({
      isSentimentReset,
      sentimentResetText,
    });
  }

  componentDidUpdate(prevProps, prevState) {
    const { storiesLength, loadingStories, storyFilters } = this.props;
    const { resetTimeFilter } = this.state;

    if (
      (storiesLength !== prevProps.storiesLength)
      || (loadingStories !== prevProps.loadingStories && !loadingStories)
    ) {
      this.fetchMoreStories();
    }
    if (
      (storyFilters.time_filter !== prevProps.storyFilters.time_filter
        || storyFilters.time_period !== prevProps.time_period
        || storyFilters.start_datetime !== prevProps.storyFilters.start_datetime
        || storyFilters.end_datetime !== prevProps.storyFilters.end_datetime)
      && resetTimeFilter && prevState.resetTimeFilter
    ) {
      this.setState({
        resetTimeFilter: false,
      }, () => {
        this.checkTimeFilter();
      });
    }
    if (
      storyFilters.sentiment_filter !== prevProps.storyFilters.sentiment_filter
      || storyFilters.sentiment_levels !== prevProps.storyFilters.sentiment_levels
      || storyFilters.sentiment_down_limit !== prevProps.storyFilters.sentiment_down_limit
      || storyFilters.sentiment_up_limit !== prevProps.storyFilters.sentiment_up_limit
    ) {
      const { isSentimentReset, sentimentResetText = '' } = this.setSentimentReset();
      this.setState({
        isSentimentReset,
        sentimentResetText,
      });
    }
  }

  componentDidCatch(error, errorInfo) {
    // You can also log the error to an error reporting service
    console.log(error, errorInfo);
  }

  componentWillUnmount() {
    this.mounted = false;
    const { actions } = this.props;
    actions.resetRecommendedStoriesData();
  }

  getCategoriesFullNames(categories) {
    const fullNames = Array.from(new Set(categories.split(',').map((item) => {
      switch (item) {
        case 'op':
        case 'mp':
          return 'News Publications';
        case 'r':
          return 'Research';
        default:
          return '';
      }
    })));
    return fullNames.join(', ').length > 30 ? `${fullNames.join(', ').slice(0, 30)}...` : fullNames.join(', ');
  }

  setSentimentReset() {
    const {
      hasSilverAccess, storyFilters, isLocationsSentiments,
    } = this.props;
    const {
      sentiment_filter: sFilter, sentiment_categories: sCategories,
      sentiment_down_limit: sDown, sentiment_up_limit: sUp,
    } = storyFilters;

    const sentimentCategories = (typeof (sCategories) === 'string') ? sCategories.split(',') : sCategories;

    if (!hasSilverAccess || isLocationsSentiments) {
      return { isSentimentReset: false };
    }
    if (sFilter === 'categories') {
      if (!sentimentCategories.length) {
        return {
          isSentimentReset: true,
          sentimentResetText: 'None',
        };
      }

      const isCategoryReset = sentimentCategories.length !== 5;
      const categoryText = sentimentCategories.map((f) => sentimentValuesByText[f]).join(', ');
      const categoryResetText = categoryText.length > 30 ? `${categoryText.slice(0, 30)}...` : categoryText;
      return {
        isSentimentReset: isCategoryReset,
        sentimentResetText: categoryResetText,
      };
    }
    if (sDown === -100 && sUp === 100) {
      return {
        isSentimentReset: false,
      };
    }
    const downText = sDown <= 0 ? sDown || 0 : `+${sDown}`;
    const upText = sUp <= 0 ? sUp || 0 : `+${sUp}`;
    return {
      isSentimentReset: true,
      sentimentResetText: `${downText}% / ${upText}%`,
    };
  }

  async fetchMoreStories() {
    const {
      query,
      activeWatchlistTopics,
      permissionsInitialized,
      watchlistPage,
      trendingPage,
      searchPage,
      seo,
      bookmarksPage,
      locations,
      parents,
      seoTopic,
      storyFilters,
      permissions: {
        features, access_levels: accessLevels,
        earliest_search_date: earliestSearchDate,
      },
      isEsgNews,
      isLocationsSentiments,
      selectedMainTimePeriod,
      isAdvancedView,
      selectedSentimentTab,
      premiumPage,
      isMajorOnly,
      whiteListId,
      actions,
    } = this.props;

    if (isEsgNews || bookmarksPage) return;

    const translateTo = storyFilters.translate_all ? 'en' : false;
    // get seo topic or all active watchlist topics for api req with topics in quotes
    const topics = seo
      ? [{ ...seoTopic, asset_class_id: (seoTopic.asset_class || {}).id }]
      // Include no more than 33 topics
      // to not exceed URL length limit (2048 characters)
      : activeWatchlistTopics.filter((item) => !item.hidden).slice(0, 33);

    const customTimeFilter = features.includes(CUSTOM_TIME_FILTER);
    const goldPlan = accessLevels.includes(GOLD);

    if (!permissionsInitialized) return;

    actions.fetchRecommendedStoriesBegin();

    const timeFilters = {
      activeTimeFilter: storyFilters.selected_time_filter,
    };
    timeFilters.timePeriod = ['months3', 'months6', 'year1', 'lifetime'].includes(storyFilters.time_period)
      ? 'month1' : storyFilters.time_period;
    const timeArr = (storyFilters.time_filter || '').split(' ');
    timeFilters.timeFilter = moment().subtract(timeArr[0], timeArr[1]).isSameOrAfter(moment().subtract(1, 'months'))
      ? storyFilters.time_filter : '1 months';

    if (locations) {
      const params = {
        parents,
        translation: translateTo,
        query,
        similarStories: storyFilters.group_similar_stories,
        customTimeFilter,
        earliestSearchDate,
      };

      if (!isAdvancedView && selectedSentimentTab !== 'all') {
        params.sentiment_levels = selectedSentimentTab;
      }
      if (isLocationsSentiments) {
        params.timeFilter = {
          timeFilter: periodTimeValues[selectedMainTimePeriod],
          activeTimeFilter: 'predefined',
        };
      } else {
        params.timeFilter = timeFilters;
      }
      actions.getLocationRecommendedStories(params);
    } else if (watchlistPage || seo) {
      actions.getWatchlistRecommendedStories({
        timeFilterValues: timeFilters,
        query,
        activeWatchlistTopics: topics,
        translation: translateTo,
        similarStories: storyFilters.group_similar_stories,
        customTimeFilter,
        goldUser: goldPlan,
        earliestSearchDate,
        sentimentLevels: (!isAdvancedView && selectedSentimentTab !== 'all') ? selectedSentimentTab : undefined,
      });
    } else if (trendingPage) {
      actions.getTrendingRecommendedStories({
        timeFilterValues: timeFilters,
        translation: translateTo,
        similarStories: storyFilters.group_similar_stories,
        customTimeFilter,
        earliestSearchDate,
        sentimentLevels: (!isAdvancedView && selectedSentimentTab !== 'all') ? selectedSentimentTab : undefined,
      });
    } else if (searchPage) {
      actions.getSearchRecommendedStories({
        timeFilterValues: timeFilters,
        query,
        translation: translateTo,
        similarStories: storyFilters.group_similar_stories,
        customTimeFilter,
        earliestSearchDate,
        sentimentLevels: (!isAdvancedView && selectedSentimentTab !== 'all') ? selectedSentimentTab : undefined,
      });
    } else if (premiumPage) {
      actions.getPremiumRecommendedStories({
        timeFilterValues: timeFilters,
        translation: translateTo,
        similarStories: storyFilters.group_similar_stories,
        customTimeFilter,
        earliestSearchDate,
        sentimentLevels: (!isAdvancedView && selectedSentimentTab !== 'all') ? selectedSentimentTab : undefined,
        whiteListId,
        isMajorOnly,
      });
    }
  }

  clearTimePeriodFilter() {
    const {
      actions,
      token,
      storyFilters,
    } = this.props;

    if (
      storyFilters.selected_time_filter === 'custom'
      || storyFilters.selected_time_filter === 'time_period'
    ) {
      this.setState({ resetTimeFilter: false });
      return actions.filterByTimePeriod({
        time_filter: 'month1',
        selected_time_filter: 'predefined',
      }, token);
    }

    let filterVal;

    switch (storyFilters.time_filter) {
      case 'minutes5':
      case 'minutes15':
      case 'hour1':
      case 'day1':
      case 'hours8':
        filterVal = 'week1';
        this.setState({ resetTimeFilter: false });
        break;
      case 'week1':
        filterVal = 'month1';
        this.setState({ resetTimeFilter: false });
        break;
      default:
        break;
    }

    setTimeout(() => {
      actions.filterByTimePeriod({
        time_filter: filterVal,
        selected_time_filter: 'predefined',
      }, token);
    });
  }

  async clearAllFilters() {
    const {
      actions, token,
      hasSilverAccess, storyFilters,
    } = this.props;
    const { resetAllFiltersAction } = actions;

    const predefinedFilterVal = 'month1';

    const dataArr = await LanguagesSvc.getLanguages();
    const languagesCodes = dataArr.map(({ iso_code: code }) => code).join(',');

    const defaultFilters = {
      all_languages: true,
      languages: languagesCodes,
      min_score: 5,
      domains_filtering_mode: 'all',
      time_filter: predefinedFilterVal,
      selected_time_filter: 'predefined',
    };

    if (hasSilverAccess) {
      if (storyFilters.sentiment_filter === 'categories') {
        defaultFilters.sentiment_categories = 'very_negative,negative,neutral,positive,very_positive';
      } else {
        defaultFilters.sentiment_down_limit = -100;
        defaultFilters.sentiment_up_limit = 100;
      }
    }

    resetAllFiltersAction(token, defaultFilters);

    // clear localstorage item for guest
    if (!token) {
      StorageSvc.removeItem('ScoreFilter');
    }
    this.setState({
      resetTimeFilter: false,
    });
  }

  checkTimeFilter() {
    const {
      storyFilters,
      isLocationsSentiments,
    } = this.props;
    const { resetTimeFilter } = this.state;

    if (resetTimeFilter || isLocationsSentiments) return;

    if (storyFilters.selected_time_filter === 'custom') {
      const formattedTimeVal = `${moment(storyFilters.start_datetime)
        .format('DD-MM-YYYY').substring(0, 10)}...`;

      return this.setState({
        prevTimeFilter: formattedTimeVal,
        nextTimeFilter: '1 month',
        resetTimeFilter: true,
      });
    }

    if (storyFilters.selected_time_filter === 'time_period') {
      const [periodValue, periodName] = storyFilters.time_period.split(' ');

      return this.setState({
        prevTimeFilter: `${periodValue} ${periodName}`,
        nextTimeFilter: '1 month',
        resetTimeFilter: true,
      });
    }

    switch (storyFilters.time_filter) {
      case 'minutes5':
        this.setState({
          prevTimeFilter: '5 minutes',
          nextTimeFilter: '1 week',
          resetTimeFilter: true,
        });
        break;
      case 'minutes15':
        this.setState({
          prevTimeFilter: '15 minutes',
          nextTimeFilter: '1 week',
          resetTimeFilter: true,
        });
        break;
      case 'hour1':
        this.setState({
          prevTimeFilter: '1 hour',
          nextTimeFilter: '1 week',
          resetTimeFilter: true,
        });
        break;
      case 'day1':
        this.setState({
          prevTimeFilter: '1 day',
          nextTimeFilter: '1 week',
          resetTimeFilter: true,
        });
        break;
      case 'hours8':
        this.setState({
          prevTimeFilter: '8 hours',
          nextTimeFilter: '1 week',
          resetTimeFilter: true,
        });
        break;
      case 'week1':
        this.setState({
          prevTimeFilter: '1 week',
          nextTimeFilter: '1 month',
          resetTimeFilter: true,
        });
        break;
      default:
        break;
    }
  }

  async handleLanguageFilter() {
    const { actions, token } = this.props;
    const {
      toggleAllLanguagesSelected,
    } = actions;

    const dataArr = await LanguagesSvc.getLanguages();
    const languagesCodes = dataArr.map(({ iso_code: code }) => code).join(',');

    toggleAllLanguagesSelected(true, languagesCodes, token);
  }

  resetSourcesFilter() {
    const { actions: { filterBySelectedSources }, token } = this.props;

    filterBySelectedSources({
      domains_filtering_mode: 'all',
    }, token);
  }

  handleLikeChange(data) {
    const { id, liked, disliked } = data;
    const { recommendedStories, actions } = this.props;
    const moreStories = recommendedStories.map((story) => {
      if (story.id === id) {
        return {
          ...story,
          liked,
          disliked,
        };
      }
      return story;
    });
    actions?.fetchRecommendedStoriesSuccess({
      recommendedStories: moreStories,
    });
  }

  handleBookmarkClick(data) {
    const { id, bookmarked } = data;
    const { recommendedStories, actions } = this.props;
    const moreStories = recommendedStories.map((story) => {
      if (story.id === id) {
        return {
          ...story,
          bookmarked,
        };
      }
      return story;
    });
    actions?.fetchRecommendedStoriesSuccess({
      recommendedStories: moreStories,
    });
  }

  render() {
    const {
      nextTimeFilter,
      prevTimeFilter,
      resetTimeFilter,
      isSentimentReset,
      sentimentResetText,
    } = this.state;
    const {
      storyFilters,
      actions,
      storiesLength,
      activatePreloader,
      piwikEnabled,
      user,
      token,
      loading,
      isEsgNews,
      classname,
      premiumPage,
      isRecommendedStoriesLoading,
      isRecommendedStoriesMoreLoading,
      recommendedStories,
      oldStories,
      slugName,
      isSEOPage,
      trendingPageStories,
      bookmarksPage,
    } = this.props;

    const { categories } = initialState;
    const { min_score: minScore } = initialState;
    const {
      filterByAllSentiments,
      filterByCategories,
      hideStory,
      freezeStories,
      filterByMinScore,
    } = actions;

    const selectedLanguagesCodes = storyFilters.languages
      ? `${storyFilters.languages.split(',').slice(0, 5).join(', ')
        .toUpperCase()}${storyFilters.languages.split(',').length > 5 ? '...' : ''}`
      : 'None';

    const selectedSources = storyFilters.categories.length
      ? this.getCategoriesFullNames(storyFilters.categories)
      : 'None';

    const recommendedStoriesClass = cx({
      'more-stories': true,
      'more-stories-margin': storiesLength,
      'no-filters-reset-options': !(
        storyFilters.categories !== categories
        || !storyFilters.all_languages
        || storyFilters.min_score !== minScore
      ),
    });

    const showApplyAllFilters = [
      storyFilters.categories !== categories,
      resetTimeFilter,
      storyFilters.domains_filtering_mode !== 'all',
      !storyFilters.all_languages,
      storyFilters.min_score !== minScore,
      isSentimentReset,
    ].filter(Boolean).length;

    const isCompactView = isMobileOnly ? (
      storyFilters.user_newsfeed_compactview
    ) : (
      storyFilters.user_newsfeed_compactview_web
    );
    const userNewsfeedView = isMobileOnly ? (
      storyFilters.user_newsfeed_view
    ) : (
      storyFilters.user_newsfeed_view_web
    );
    const isTilesView = userNewsfeedView === 'tiles' && !isCompactView;

    const moreStories = differenceBy(recommendedStories, oldStories, 'id');

    return (
      <div className={recommendedStoriesClass}>
        {!activatePreloader && (
          <div className={cx('more-stories-widget')}>
            <div className={cx('more-stories-widget-head')}>
              <div className={cx('details', { bookmarks: bookmarksPage })}>
                {bookmarksPage ? (
                  <div className={cx('bookmarks_none')}>
                    <FontAwesomeIcon className={cx('bookmarks_none_icon')} icon={faBookmark} />
                    {
                    (resetTimeFilter
                      || storyFilters.categories !== categories
                      || !storyFilters.all_languages
                      || storyFilters.min_score !== minScore)
                      ? 'No bookmarks for the current filters.'
                      : 'No stories have been bookmarked yet.'
                    }
                  </div>
                ) : (
                  <h2 className={cx('title')}>
                    {storiesLength ? (
                      'Little content was found that meets your criteria'
                    ) : (
                      'No content was found that meets your criteria'
                    )}
                  </h2>
                )}
                {(
                  resetTimeFilter
                  || storyFilters.categories !== categories
                  || !storyFilters.all_languages
                  || storyFilters.min_score !== minScore
                )
                  && (
                    <div className={cx('reset-filters-wrapper')}>
                      <p className={cx('sub-title')}>Try these filter settings</p>
                      <ul className={cx('options')}>
                        {resetTimeFilter && (
                          <li onClick={this.clearTimePeriodFilter}>
                            <p>Increase time period</p>
                            <span>
                              {prevTimeFilter}
                              <FontAwesomeIcon icon={faLongArrowRight} />
                              {nextTimeFilter}
                            </span>
                          </li>
                        )}
                        {(storyFilters.categories.split(',').sort().join(',') !== categories.split(',').sort().join(',') && !premiumPage) && (
                          <li onClick={() => filterByCategories(categories, token)}>
                            <p>
                              Select all content types
                            </p>
                            <span>
                              {selectedSources}
                              <FontAwesomeIcon icon={faLongArrowRight} />
                              All
                            </span>
                          </li>
                        )}
                        {(storyFilters.domains_filtering_mode !== 'all' && !premiumPage) && (
                          <li onClick={this.resetSourcesFilter}>
                            <p>
                              Reset selected/blocked sources
                            </p>
                            <span>
                              {storyFilters.domains_filtering_mode === 'whitelist' ? 'Selected ' : 'Blocked '}
                              sources
                              <FontAwesomeIcon icon={faLongArrowRight} />
                              All sources
                            </span>
                          </li>
                        )}
                        {!storyFilters.all_languages && (
                          <li
                            onClick={this.handleLanguageFilter}
                          >
                            <p>
                              Select all languages
                            </p>
                            <span>
                              {selectedLanguagesCodes}
                              <FontAwesomeIcon icon={faLongArrowRight} />
                              All
                            </span>
                          </li>
                        )}
                        {storyFilters.min_score !== minScore && storyFilters.min_score > 0 && (
                          <li onClick={() => filterByMinScore(minScore, token)}>
                            <p>
                              {storyFilters.min_score < minScore ? 'Increase' : 'Decrease'}
                              &nbsp;
                              minimum CityFALCON Score
                            </p>
                            <span>
                              {storyFilters.min_score}
                              %
                              <FontAwesomeIcon icon={faLongArrowRight} />
                              5%
                            </span>
                          </li>
                        )}
                        {isSentimentReset && (
                          <li onClick={() => filterByAllSentiments(token)}>
                            <p>Select all sentiment values</p>
                            <span>
                              {sentimentResetText}
                              <FontAwesomeIcon icon={faLongArrowRight} />
                              All
                            </span>
                          </li>
                        )}
                        {showApplyAllFilters > 1 && (
                          <li className={cx('clear-all')} onClick={this.clearAllFilters}>
                            <p>Apply All Recommended Filters</p>
                          </li>
                        )}
                      </ul>
                    </div>
                  )}
              </div>
            </div>
          </div>
        )}
        <div
          className={
            cx('more-stories-recommended', {
              'recommended-stories-loading': isRecommendedStoriesLoading,
              tiles: isTilesView,
              esg_news: isEsgNews,
            })
          }
        >
          {Boolean(moreStories.length) && (
            <span className={cx('more-stories-recommended-title')}>Here are some results that are close</span>
          )}

          {isRecommendedStoriesLoading ? (
            <Preloader loading static />
          ) : (
            <>
              <div className={cx({ tiles_wrapper: isTilesView }, classname)}>
                {moreStories
                  .slice(0, 30)
                  .map((story) => (
                    <StoryCard
                      isRecommendedStories
                      key={story.id}
                      type={story.type}
                      story={story}
                      onHide={hideStory}
                      onFreezeStories={freezeStories}
                      onLikeChanged={this.handleLikeChange}
                      onBookmarkClick={this.handleBookmarkClick}
                      piwikEnabled={piwikEnabled}
                      user={user}
                      loading={loading}
                      slugName={slugName}
                      isSEOPage={isSEOPage}
                      trendingPageStories={trendingPageStories}
                    />
                  ))}
              </div>
              {isRecommendedStoriesMoreLoading && (
                <Preloader
                  loading
                  static
                  centered
                />
              )}
            </>
          )}
        </div>
        {
          Boolean(moreStories.length) && !isRecommendedStoriesMoreLoading && (
            <StoryCaughtUp className={cx('more-story-caught')} hideButton />
          )
        }
      </div>
    );
  }
}

RecommendedStories.defaultProps = {
  parent: null,
  locations: false,
  seo: false,
  searchPage: false,
  trendingPage: false,
  watchlistPage: false,
  parents: [],
};

function mapStateToProps({
  stories,
  storyFilters,
  subscriptions,
  watchlistReducer,
  userPreferencesReducer,
  locationsSentiment,
  recommendedStories,
}) {
  const { permissions, permissionsInitialized } = subscriptions;
  const timeFilter = permissions.search_period;
  const hasSilverAccess = permissions.access_levels.includes(SILVER);

  return {
    loading: stories.loading,
    stories: stories.cards,
    freezeStories: stories.freeze_stories,
    activatePreloader: stories.activatePreloader,
    storyFilters,
    timeFilter,
    permissionsInitialized,
    activeWatchlistTopics: watchlistReducer.activeWatchlistTopics,
    token: watchlistReducer.userToken,
    user: userPreferencesReducer.user,
    permissions,
    hasSilverAccess,
    selectedMainTimePeriod: locationsSentiment.selectedMainTimePeriod,
    selectedSentimentTab: storyFilters.selected_sentiment_tab,
    isRecommendedStoriesLoading: recommendedStories.loading,
    isRecommendedStoriesMoreLoading: recommendedStories.isNewLoading,
    recommendedStories: recommendedStories.recommendedStories,
  };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators({
      ...storyActions, ...storyFiltersAction, ...recommendedStoriesAction,
    }, dispatch),
  };
}

export default withComponentName(
  connect(
    mapStateToProps, mapDispatchToProps, null, { forwardRef: true },
  )(permissionsDecorator(RecommendedStories)),
);
